import { CalendarIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { TimeList } from "./TimeList";
import { MinuteStep, time } from "@/lib/time";
import { cn } from "@/lib/utils";

export interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  minuteStep?: MinuteStep;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
}

const parseLocalDate = (dateString: string): Date | undefined => {
  if (!dateString) return undefined;
  const [y, m, d] = dateString.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const formatLocalDate = (date: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const DateTimePicker = ({
  value,
  onChange,
  minuteStep = 15,
  placeholder = "-",
  disabled,
  className,
  contentClassName,
}: DateTimePickerProps) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { date, time: timeValue } = useMemo(
    () => time.splitDateTime(value),
    [value],
  );

  const handleDateSelect = (next: Date | undefined) => {
    if (!next) return;
    const nextDate = formatLocalDate(next);
    const nextTime = timeValue || time.nowRounded(minuteStep);
    onChange(time.joinDateTime(nextDate, nextTime));
    inputRef.current?.focus();
  };

  const handleTimeSelect = (slotValue: string) => {
    const nextDate = date || time.todayDate();
    onChange(time.joinDateTime(nextDate, slotValue));
    setOpen(false);
  };

  const triggerLabel = (() => {
    if (!value) return placeholder;
    if (date && timeValue) {
      return `${time.formatDateLabel(date)}  ·  ${time.formatLabel(timeValue)}`;
    }
    if (date) return time.formatDateLabel(date);
    if (timeValue) return time.formatLabel(timeValue);
    return placeholder;
  })();

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-slate-500",
          className,
        )}
      >
        <span>{triggerLabel}</span>
        <CalendarIcon className="h-4 w-4 text-slate-500" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("flex w-auto gap-0 p-0", contentClassName)}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <Calendar
          mode="single"
          selected={parseLocalDate(date)}
          onSelect={handleDateSelect}
          defaultMonth={parseLocalDate(date)}
        />
        <div className="flex w-48 flex-col border-l border-slate-200">
          <TimeList
            value={timeValue}
            onSelect={handleTimeSelect}
            minuteStep={minuteStep}
            inputRef={inputRef}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
